/**
 * Payment Checkout Modal/Component
 * Displays wePAY QR code and polls for payment status
 * Shows after user confirms purchase
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, AlertCircle, Loader2, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

interface PaymentCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  destRef: string;
  amount: number;
  gameName: string;
  packageName: string;
  onPaymentSuccess: () => void;
}

export function PaymentCheckout({
  isOpen,
  onClose,
  destRef,
  amount,
  gameName,
  packageName,
  onPaymentSuccess,
}: PaymentCheckoutProps) {
  const [paymentStatus, setPaymentStatus] = useState<"pending" | "processing" | "completed" | "failed">("pending");
  const [qrCode, setQrCode] = useState<string>("");
  const [transactionId, setTransactionId] = useState<string>("");
  const [pollingCount, setPollingCount] = useState(0);
  const maxPollingAttempts = 120; // 2 minutes with 1-second intervals

  // Query payment status
  const { data: statusData, isLoading: statusLoading } = trpc.payment.getStatus.useQuery(
    { destRef },
    {
      enabled: isOpen && paymentStatus === "pending",
      refetchInterval: 2000, // Poll every 2 seconds
      refetchIntervalInBackground: true,
    }
  );

  // Update payment status based on query result
  useEffect(() => {
    if (!statusData) return;

    const newStatus = statusData.status as "pending" | "processing" | "completed" | "failed";
    setPaymentStatus(newStatus);
    setTransactionId(statusData.transactionId?.toString() || "");

    if (statusData.responseData?.qrCode) {
      setQrCode(statusData.responseData.qrCode);
    }

    if (newStatus === "completed") {
      toast.success("ชำระเงินสำเร็จ!", {
        description: `เติม${gameName} เสร็จแล้ว`,
      });
      setTimeout(() => {
        onPaymentSuccess();
        onClose();
      }, 2000);
    } else if (newStatus === "failed") {
      toast.error("ชำระเงินไม่สำเร็จ", {
        description: "กรุณาลองใหม่อีกครั้ง",
      });
    }

    setPollingCount((prev) => prev + 1);
  }, [statusData, gameName, onPaymentSuccess, onClose]);

  // Stop polling after max attempts
  useEffect(() => {
    if (pollingCount > maxPollingAttempts && paymentStatus === "pending") {
      setPaymentStatus("failed");
      toast.error("หมดเวลารอการชำระเงิน", {
        description: "กรุณาลองใหม่อีกครั้ง",
      });
    }
  }, [pollingCount, paymentStatus]);

  const handleCopyRef = () => {
    navigator.clipboard.writeText(destRef);
    toast.success("คัดลอก Reference แล้ว");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg max-w-md w-full p-6 border-3 border-black"
            style={{ boxShadow: "6px 6px 0px black" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg">ชำระเงิน</h2>
              <button
                onClick={onClose}
                className="p-1 hover:bg-black/10 rounded"
              >
                <X size={20} />
              </button>
            </div>

            {/* Status Indicator */}
            <div className="mb-6 text-center">
              {paymentStatus === "pending" && (
                <div className="space-y-3">
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 flex items-center justify-center mx-auto rounded-lg">
                    <Loader2 size={32} className="animate-spin" />
                  </div>
                  <p className="font-mono text-sm text-black/60">รอการชำระเงิน...</p>
                </div>
              )}

              {paymentStatus === "processing" && (
                <div className="space-y-3">
                  <div className="w-16 h-16 bg-yellow-100 text-yellow-600 flex items-center justify-center mx-auto rounded-lg">
                    <Loader2 size={32} className="animate-spin" />
                  </div>
                  <p className="font-mono text-sm text-black/60">กำลังประมวลผล...</p>
                </div>
              )}

              {paymentStatus === "completed" && (
                <div className="space-y-3">
                  <div className="w-16 h-16 bg-green-100 text-green-600 flex items-center justify-center mx-auto rounded-lg">
                    <Check size={32} />
                  </div>
                  <p className="font-mono text-sm text-black/60">ชำระเงินสำเร็จ!</p>
                </div>
              )}

              {paymentStatus === "failed" && (
                <div className="space-y-3">
                  <div className="w-16 h-16 bg-red-100 text-red-600 flex items-center justify-center mx-auto rounded-lg">
                    <AlertCircle size={32} />
                  </div>
                  <p className="font-mono text-sm text-black/60">ชำระเงินไม่สำเร็จ</p>
                </div>
              )}
            </div>

            {/* Order Details */}
            <div className="border-t-2 border-black pt-4 mb-4 space-y-2">
              <div className="flex justify-between font-mono text-xs">
                <span className="text-black/50">เกม</span>
                <span className="font-bold">{gameName}</span>
              </div>
              <div className="flex justify-between font-mono text-xs">
                <span className="text-black/50">แพ็คเกจ</span>
                <span className="font-bold">{packageName}</span>
              </div>
              <div className="flex justify-between font-heading font-bold text-sm border-t-2 border-black pt-2 mt-2">
                <span>รวม</span>
                <span>C {amount.toLocaleString("th-TH")}</span>
              </div>
            </div>

            {/* Reference Number */}
            {destRef && (
              <div className="mb-4 p-3 bg-black/5 border-2 border-black/20 rounded">
                <p className="font-mono text-[10px] text-black/50 mb-1">Reference</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono font-bold text-xs break-all">{destRef}</code>
                  <button
                    onClick={handleCopyRef}
                    className="p-1 hover:bg-black/10 rounded shrink-0"
                    title="Copy reference"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* QR Code (if available) */}
            {qrCode && paymentStatus === "pending" && (
              <div className="mb-4 text-center">
                <img
                  src={qrCode}
                  alt="Payment QR Code"
                  className="w-48 h-48 mx-auto border-2 border-black"
                />
                <p className="font-mono text-xs text-black/50 mt-2">
                  สแกน QR Code เพื่อชำระเงิน
                </p>
              </div>
            )}

            {/* Polling Status */}
            {paymentStatus === "pending" && (
              <div className="mb-4 text-center">
                <p className="font-mono text-xs text-black/40">
                  รอการชำระเงิน ({pollingCount}/{maxPollingAttempts})
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              {paymentStatus === "completed" && (
                <button
                  onClick={() => {
                    onPaymentSuccess();
                    onClose();
                  }}
                  className="btn-brutal bg-black text-white w-full text-center text-xs"
                >
                  ปิด
                </button>
              )}

              {paymentStatus === "failed" && (
                <>
                  <button
                    onClick={onClose}
                    className="btn-brutal bg-white text-black flex-1 text-center text-xs"
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={onClose}
                    className="btn-brutal bg-black text-white flex-1 text-center text-xs"
                  >
                    ลองใหม่
                  </button>
                </>
              )}

              {(paymentStatus === "pending" || paymentStatus === "processing") && (
                <button
                  onClick={onClose}
                  className="btn-brutal bg-white text-black w-full text-center text-xs"
                >
                  ปิด (รอการชำระเงิน)
                </button>
              )}
            </div>

            {/* Help Text */}
            <p className="font-mono text-[10px] text-black/40 mt-4 text-center">
              ระบบจะตรวจสอบสถานะการชำระเงินโดยอัตโนมัติ
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
